import { useEffect, useState } from 'react';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';

/**
 * Carries the portal's documentation styling into the TechDocs reader
 * (DX-275).
 *
 * TechDocs renders a built MkDocs page inside a **shadow root**
 * (`attachShadow` in plugin-techdocs-react). A shadow root is a style boundary:
 * nothing in the app's own stylesheet crosses it. So `theme/techdocs.css` —
 * the reading column, the heading scale, the code blocks — never reached a
 * documentation page at all. It applied only to the docs *index*, which is
 * ordinary DOM, which is why the styling looked partly applied rather than
 * absent.
 *
 * The one rule that made this obvious: a heading jumped to from the search or
 * from "On this page" landed underneath the top bar, because the
 * `scroll-margin-top` meant to offset it sat outside the boundary.
 *
 * An addon is the supported way in. It runs inside the reader, gets the shadow
 * root, and appends a stylesheet there — the same rules, on the correct side of
 * the boundary. Selectors drop the `.techdocs-content` prefix: inside the
 * shadow root the MkDocs markup is the whole document.
 */

const SHADOW_STYLES = `
/* Where a jump lands. Following a search result, or a heading in the
   "On this page" column, scrolls the target to the very top of the scroll
   container. That container is the page inside the frame, whose top edge is
   already below the floating bar (DX-352), so the heading needs only a little
   air above it rather than the bar's height as well. */
.md-typeset h1,
.md-typeset h2,
.md-typeset h3,
.md-typeset h4,
.md-typeset h5,
.md-typeset h6,
[id]:target {
  scroll-margin-top: 1.5rem;
}

/* The document's navigation and its table of contents, put back beside the
   text (DX-352). The reader fixes both columns and sets their top to where
   the document starts IN THE WINDOW (plugin-techdocs,
   useTechDocsReaderDom). Inside the frame a fixed element is placed against
   the frame, not the window (contain: layout in Root.tsx), so that
   distance was counted twice: both columns sat one bar's height too low, and
   the table of contents ran into the reader's "Next" link at the bottom. The
   frame starts --ui-shell-top below the window's top, and this takes it
   back. Only where the reader draws them as columns — below 76.25em it turns
   them into a drawer and sets \`top\` to 0 itself. */
@media screen and (min-width: 76.25em) {
  .md-sidebar {
    margin-top: calc(-1 * var(--ui-shell-top, 0px));
  }

  /* The two columns at the Developer Portal's proportions rather than
     Backstage's 16rem each (DX-352). Inside the frame, beside the portal's
     own navigation, two 16rem columns left the text about 30rem wide. The
     portal keeps its "On this page" column narrow and cuts a long heading
     short rather than wrapping it; this does the same, and gives the
     document's navigation a little less than Backstage did. Measured from
     plugin-techdocs' layout rules, which these follow in shape. */
  .md-sidebar--primary,
  .md-sidebar--primary .md-sidebar__scrollwrap {
    width: 14rem;
  }
  .md-sidebar--secondary,
  .md-sidebar--secondary .md-sidebar__scrollwrap {
    width: 11rem;
  }
  .md-content {
    margin-left: 14rem;
    max-width: calc(100% - 14rem - 11rem);
  }
  .md-content > .md-sidebar {
    left: 14rem;
  }
  .md-sidebar--secondary .md-nav__link .md-ellipsis,
  .md-sidebar--secondary .md-nav__link {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* The link is a flex row, and a flex item will not shrink below its text
     unless told it may — without this nothing is cut short. */
  .md-sidebar--secondary .md-nav__link .md-ellipsis {
    min-width: 0;
  }
}

/* "On this page", as the Developer Portal draws it (DX-352): a quiet card,
   the heading in the navigation's text-mono style under the portal's own
   words, a hairline rail down the entries, and the current one in the brand
   violet rather than underlined. The heading keeps mkdocs' text in the page
   — it labels the list for a screen reader — and only its face changes. */
.md-sidebar--secondary .md-sidebar__scrollwrap {
  box-sizing: border-box;
  padding: 1.25rem 1rem;
  border-radius: 0.5rem;
  background: var(--docs-toc-bg, rgba(255, 255, 255, 0.5));
}
.md-nav--secondary > .md-nav__title {
  padding: 0 0 0.75rem;
  background: none;
  box-shadow: none;
  font-size: 0;
  font-weight: 500;
}
.md-nav--secondary > .md-nav__title::before {
  content: 'On this page';
  font-size: 0.6875rem;
  line-height: 1.15;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--docs-toc-title, #3b364c);
}
.md-nav--secondary > .md-nav__list {
  border-left: 1px solid var(--docs-toc-rail, rgba(0, 0, 0, 0.08));
}
.md-nav--secondary .md-nav__list .md-nav__list {
  padding-left: 0.75rem;
}
.md-nav--secondary .md-nav__item {
  padding: 0;
}
.md-nav--secondary .md-nav__link {
  margin: 0 0 0 -1px;
  padding: 0.25rem 0 0.25rem 0.75rem;
  border-left: 1px solid transparent;
  font-size: 0.875rem;
  line-height: 1.25rem;
  text-decoration: none;
  color: var(--docs-toc-link, rgba(11, 5, 34, 0.8));
  transition: color 160ms ease;
}
.md-nav--secondary .md-nav__link:hover {
  color: var(--docs-toc-link-active, #620dff);
}
.md-nav--secondary .md-nav__link--active,
.md-nav--secondary .md-nav__item--active > .md-nav__link {
  border-left-color: var(--docs-toc-link-active, #620dff);
  font-weight: 500;
  text-decoration: none;
  color: var(--docs-toc-link-active, #620dff);
}

/* The reading column: prose capped at a readable width rather than running
   the full width of a wide screen. */
.md-content__inner,
.md-typeset {
  max-width: var(--docs-prose-max, 56rem);
  color: var(--docs-text-prose, #241c3d);
  font-size: 1rem;
  line-height: 1.52;
}

.md-typeset > :first-child {
  margin-top: 0;
}

/* Headings. h2 carries the brand violet, as in the reference portal: the one
   place colour marks structure rather than decorating it. */
.md-typeset h1 {
  font-size: clamp(2.5rem, 4vw, 3.65rem);
  line-height: 0.98;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--docs-text-title, #2c1c65);
  margin-bottom: 0.75rem;
}

.md-typeset h2 {
  margin-top: 2.35rem;
  margin-bottom: 0.9rem;
  font-size: clamp(2rem, 3vw, 2.6rem);
  line-height: 1.05;
  font-weight: 700;
  color: var(--docs-text-prose-h2, #620dff);
  border-bottom: none;
}

.md-typeset h3 {
  margin-top: 1.9rem;
  margin-bottom: 0.75rem;
  font-size: 1.55rem;
  line-height: 1.2;
  font-weight: 600;
  color: var(--docs-text-prose-h3, #392071);
}

.md-typeset p,
.md-typeset li {
  font-size: 1rem;
  line-height: 1.52;
}

.md-typeset a {
  color: var(--ui-primary, #620dff);
  text-decoration: none;
}

.md-typeset a:hover {
  text-decoration: underline;
}

/* Code blocks, with the copy button in a header strip along the top — the
   arrangement the reference portal uses. */
.md-typeset .highlight {
  position: relative;
  border: 1px solid var(--docs-border-subtle, #e6e1f2);
  border-radius: 0.75rem;
  overflow: hidden;
  padding-top: 2.6rem;
}

.md-typeset .highlight::before {
  content: 'Code';
  position: absolute;
  inset: 0 0 auto 0;
  height: 2.6rem;
  display: flex;
  align-items: center;
  padding-left: 1rem;
  background: var(--ui-bg-muted, #edecf3);
  border-bottom: 1px solid var(--docs-border-subtle, #e6e1f2);
  color: var(--docs-card-muted, #5f587b);
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.md-typeset .language-bash.highlight::before,
.md-typeset .language-sh.highlight::before,
.md-typeset .language-shell.highlight::before,
.md-typeset .language-console.highlight::before {
  content: 'Terminal';
}
.md-typeset .language-yaml.highlight::before { content: 'YAML'; }
.md-typeset .language-json.highlight::before { content: 'JSON'; }
.md-typeset .language-ts.highlight::before,
.md-typeset .language-typescript.highlight::before { content: 'TypeScript'; }
.md-typeset .language-js.highlight::before,
.md-typeset .language-javascript.highlight::before { content: 'JavaScript'; }
.md-typeset .language-text.highlight::before { content: ''; }

.md-clipboard {
  position: absolute;
  top: 0.55rem;
  right: 0.6rem;
  z-index: 2;
  width: 1.6rem;
  height: 1.6rem;
  display: grid;
  place-items: center;
  color: var(--docs-card-muted, #5f587b);
  border-radius: 0.25rem;
}

.md-clipboard:hover,
.md-clipboard:focus-visible {
  color: var(--ui-primary, #620dff);
  background-color: color-mix(in srgb, var(--ui-primary, #620dff) 12%, transparent);
}

.md-clipboard--success {
  color: var(--ui-primary, #620dff);
}

.md-typeset .highlight pre,
.md-typeset .highlight pre > code {
  border: none;
  border-radius: 0;
  margin: 0;
}

.md-typeset code {
  background-color: var(--ui-bg-muted, #edecf3);
  color: var(--docs-text-prose, #241c3d);
  border-radius: 0.25rem;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.875em;
  padding: 0.15em 0.4em;
}

/* Tables scroll in their own container, so the page never scrolls sideways. */
.md-typeset__table,
.md-typeset .md-typeset__scrollwrap {
  overflow-x: auto;
}

.md-typeset table:not([class]) {
  border: 1px solid var(--docs-border-subtle, #e6e1f2);
  border-radius: 0.5rem;
  font-size: 14px;
}

.md-typeset table:not([class]) th {
  background-color: var(--ui-bg-muted, #edecf3);
  color: var(--docs-text-title, #2c1c65);
  font-weight: 600;
}

/* Admonitions read as an aside: an accent rule, not a second design. */
.md-typeset .admonition,
.md-typeset details {
  background-color: var(--ui-bg-muted, #edecf3);
  border: 1px solid var(--docs-border-subtle, #e6e1f2);
  border-left: 3px solid var(--ui-primary, #620dff);
  border-radius: 0.5rem;
  box-shadow: none;
  font-size: 1rem;
}

.md-typeset blockquote {
  border-left: 3px solid var(--docs-border-subtle, #e6e1f2);
  color: var(--docs-card-muted, #5f587b);
  margin-left: 0;
}
`;

export const ShadowStyles = () => {
  const shadowRoot = useShadowRoot();
  const themeApi = useApi(appThemeApiRef);
  const [themeId, setThemeId] = useState(themeApi.getActiveThemeId());

  /*
    The token values below are COPIED onto the shadow host, so they do not
    update by themselves when the reader switches appearance — the docs would
    stay frozen in the mode they were opened in, which is exactly what AC-3
    promises they will not do. Following the theme id re-runs the copy.
  */
  useEffect(() => {
    const sub = themeApi.activeThemeId$().subscribe(id => setThemeId(id));
    return () => sub.unsubscribe();
  }, [themeApi]);

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    const style = document.createElement('style');
    style.dataset.revenexx = 'techdocs';
    style.textContent = SHADOW_STYLES;
    shadowRoot.appendChild(style);

    /*
      The tokens live on the app's :root, which the shadow root cannot see
      either. Copying the handful the rules above read keeps one source of
      truth — they are still decided in tokens.css — without pulling the whole
      token layer across the boundary.
    */
    const host = shadowRoot.host as HTMLElement | null;
    if (host) {
      const app = getComputedStyle(document.documentElement);
      for (const name of [
        '--ui-primary',
        '--ui-shell-top',
        '--ui-bg-muted',
        '--docs-prose-max',
        '--docs-text-prose',
        '--docs-text-title',
        '--docs-text-prose-h2',
        '--docs-text-prose-h3',
        '--docs-card-muted',
        '--docs-border-subtle',
        '--docs-toc-bg',
        '--docs-toc-title',
        '--docs-toc-link',
        '--docs-toc-link-active',
        '--docs-toc-rail',
        '--font-mono',
      ]) {
        const value = app.getPropertyValue(name).trim();
        if (value) {
          host.style.setProperty(name, value);
        }
      }
    }

    return () => style.remove();
  }, [shadowRoot, themeId]);

  return null;
};
